import type {
    Course,
    DiagnosticTest,
    Faq,
    Feature,
    Leader,
    NavItem,
    Stat,
    Testimonial
} from '@/types'

import {
    BriefcaseBusiness,
    Car,
    ClipboardCheck,
    Compass,
    GraduationCap,
    Handshake,
    Headset,
    Landmark,
    RotateCcw,
    ShieldCheck,
    Sparkles,
    TrendingUp,
    Users,
    Video,
    Wallet
} from 'lucide-react'

const WHATSAPP_NUMBER = '917205529499'

export const whatsappLink = (message: string): string => {
    return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`
}

export const LINKS = {
    learningPortal: 'https://learn.skillkafe.in/',
    playStore: 'https://play.google.com/store/apps/details?id=com.edmingle.saigarudaeducation',
    eduKafe: 'https://edukafe.in/',
    privacy: 'https://www.skillkafe.in/privacy-policy/',
    terms: 'https://www.skillkafe.in/terms-conditions/',
    blog: 'https://www.skillkafe.in/blog/',
    facebook: 'https://www.facebook.com/profile.php?id=61567854164755',
    instagram: 'https://www.instagram.com/skill.kafe/',
    linkedin: 'https://www.linkedin.com/company/105012378/',
    whatsappLearner: whatsappLink("Hi Skillkafe, I'm looking for a job and would like to know more about your courses."),
    whatsappRecruiter: whatsappLink("Hi Skillkafe, I'm a recruiter and would like to hire trained candidates.")
}

export const FOUNDER_VIDEO_ID = '_FqnI20q9CI'

export const CONTACT = {
    phoneDisplay: '+91 90882 03800',
    phoneHref: 'tel:+919088203800',
    whatsappDisplay: '+91 72055 29499',
    email: 'support@skillkafe.in',
    addressLines: ["20C Ballygunge Station Road", "Indroneel Building, Ground Floor", "Kolkata 700019"],
    mapsHref: 'https://www.google.com/maps/search/?api=1&query=20C%20Ballygunge%20Station%20Road%2C%20Kolkata%20700019'
}

export const NAV_ITEMS: NavItem[] = [
    { label: "Courses", href: '#courses' },
    { label: "How it works", href: '#how-it-works' },
    { label: "For recruiters", href: '#recruiters' },
    { label: "Success stories", href: '#stories' },
    { label: "FAQ", href: '#faq' }
]

export const HERO_STATS: Stat[] = [
    { value: "300+", label: "companies trust Skillkafe" },
    { value: "22", label: "high-hiring sectors and roles" },
    { value: "4.5/5", label: "average learner rating" }
]

export const HIRING_SECTORS: string[] = [
    "Banking",
    "Financial Services",
    "Microfinance",
    "Insurance",
    "Tele Sales",
    "Customer Support",
    "Organised Retail",
    "Healthcare Support",
    "Data Entry",
    "In-house Customer Service",
    "Tele Customer Support",
    "Finance and Accounting",
    "Field Sales",
    "Office Administration",
    "HR Support",
    "Electrical Sales and Service",
    "Automobile Service Workshops",
    "Hotels and Restaurants",
    "Automobile Showrooms",
    "Real Estate",
    "Logistics Support",
    "In-store Sales"
]

export const COURSES: Course[] = [
    {
        id: 'auto-aspire',
        name: "Auto Aspire",
        sector: "Automobile industry",
        summary: "Training for customer-facing roles in automobile dealer showrooms and service workshops, with placement assistance when you finish.",
        workplaces: ["Dealer showrooms", "Service workshops", "Customer-facing roles"],
        href: 'https://learn.skillkafe.in/courses?categories_ids=9203',
        icon: Car,
        tone: 'orange'
    },
    {
        id: 'bfsi-aspire',
        name: "BFSI Aspire",
        sector: "Banking, financial services and insurance",
        summary: "Build the skills banks, financial services firms and insurers look for in customer-facing staff, with placement assistance included.",
        workplaces: ["Banks", "Financial services and microfinance", "Insurance"],
        href: 'https://learn.skillkafe.in/courses?categories_ids=9990',
        icon: Landmark,
        tone: 'indigo'
    },
    {
        id: 'customer-relationship',
        name: "Customer Relationship",
        sector: "Retail, hospitality and events",
        summary: "Learn to serve customers with confidence in organised retail, hospitality and event management, with placement support to get you started.",
        workplaces: ["Organised retail", "Hospitality", "Event management"],
        href: 'https://learn.skillkafe.in/courses?categories_ids=9497',
        icon: Headset,
        tone: 'teal'
    }
]

export const JOURNEY_STEPS: Feature[] = [
    {
        title: "Clear a short screening",
        description: "Our HR team looks at your communication, adaptability and job orientation, so every learner starts ready to commit.",
        icon: ClipboardCheck
    },
    {
        title: "Train for your sector",
        description: "Industry-focused modules, video lessons and practical training built around the customer-facing role you want.",
        icon: GraduationCap
    },
    {
        title: "Learn from experts, live",
        description: "Interactive live sessions with industry leaders, interview preparation and soft skills, plus faculty support throughout.",
        icon: Video
    },
    {
        title: "Meet hiring companies",
        description: "We connect you with recruiting agencies and corporates that are hiring, especially in tier 2 and tier 3 cities.",
        icon: BriefcaseBusiness
    }
]

export const WHY_FEATURES: Feature[] = [
    {
        title: "Flexible and affordable",
        description: "Study online on your own schedule with affordable modules. No travel and no expensive degree needed.",
        icon: Wallet
    },
    {
        title: "Live expert sessions",
        description: "Interact with industry leaders in live sessions and get faculty support all the way through your course.",
        icon: Users
    },
    {
        title: "Revisit lessons anytime",
        description: "Recorded sessions, videos and e-learning content you can repeat as often as you need.",
        icon: RotateCcw
    },
    {
        title: "Guidance that fits you",
        description: "Personalised career path guidance matches your strengths and interests with what employers need.",
        icon: Compass
    },
    {
        title: "Grooming for the corporate world",
        description: "Interview preparation and soft skills training, so you walk in confident on your first day.",
        icon: Sparkles
    },
    {
        title: "Placement assistance",
        description: "Every course comes with placement assistance through our network of recruiting agencies and corporates.",
        icon: BriefcaseBusiness
    }
]

export const DIAGNOSTIC_TESTS: DiagnosticTest[] = [
    { code: 'CFT', title: "CFT Diagnostic Test", href: 'https://psychometric.skillkafe.in/' },
    { code: 'CGT', title: "CGT Diagnostic Test", href: 'https://psychometric.skillkafe.in/cgt' }
]

export const RECRUITER_BENEFITS: Feature[] = [
    {
        title: "Pre-screened talent pool",
        description: "Candidates are assessed for your industry skill needs, adaptability and culture fit before they reach you.",
        icon: ShieldCheck
    },
    {
        title: "Industry-specific training",
        description: "Programmes built with industry experts, so new hires need less onboarding and no remedial training.",
        icon: GraduationCap
    },
    {
        title: "Job fitment, high retention",
        description: "Technical and soft skills plus local hiring in tier 2 and tier 3 cities help people stay and grow with you.",
        icon: TrendingUp
    },
    {
        title: "Recruitment, your way",
        description: "Dedicated hiring campaigns, targeted candidate searches and onboarding support, run closely with your HR team.",
        icon: Handshake
    }
]

export const HIRING_STEPS: Feature[] = [
    {
        title: "Share your requirement",
        description: "Tell us the roles, locations and number of openings you need to fill.",
        icon: ClipboardCheck
    },
    {
        title: "We screen and assess",
        description: "Our HR team checks skills, adaptability and culture fit for your roles.",
        icon: ShieldCheck
    },
    {
        title: "Meet your shortlist",
        description: "Interview a curated shortlist of trained, job-ready candidates.",
        icon: Users
    }
]

export const TESTIMONIALS: Testimonial[] = [
    {
        name: "Snehashree Sarangi",
        label: "BFSI Aspire learner",
        rating: 4.3,
        quote: "Skillkafe has introduced me to BFSI Sector through BFSI Aspire Training curriculum."
    },
    {
        name: "Pallavi Pridarsini Dash",
        label: "Job seeker",
        rating: 4.9,
        quote: "Skill-kafe is a platform that works for the benefits of jobseekers."
    },
    {
        name: "Smruti Rani Dash",
        label: "Auto Aspire learner",
        rating: 4.0,
        quote: "I did my Auto Aspire course from Skill Kafe and it helps me a lot to improve myself."
    },
    {
        name: "Rojalin Parida",
        label: "Job seeker",
        rating: 4.8,
        quote: "Skill-Kafe is a best platform for innovative learning method."
    }
]

export const LEADERSHIP: Leader[] = [
    {
        name: "Sukesh Dash",
        role: "Founder and CEO",
        bio: "Close to 20 years in corporate learning and development, working with more than 25,000 learners."
    },
    {
        name: "Smarajit Mishra",
        role: "Chief Strategist and Chief Learning Officer",
        bio: "Over 20 years in learning and development. Fellow of the Insurance Institute of India."
    },
    {
        name: "Nihar Ranjan Dash",
        role: "Chief Operating Officer",
        bio: "About 25 years in business operations and administration. Holds a law degree and is a Cost Accountant."
    },
    {
        name: "Sanjit Samal",
        role: "Member and Consultant",
        bio: "Established My Event India, specialising in large corporate events, and has been with Skillkafe since day one."
    },
    {
        name: "K. Md. Rafi",
        role: "Member and Consultant",
        bio: "Engineer and CEO of MAHE Hydraulic Solutions, leading its Middle East operations with a global outlook."
    }
]

export const FAQS: Faq[] = [
    {
        question: "What courses does Skillkafe offer?",
        answer: "Job-ready courses for customer-facing roles: Auto Aspire for the automobile industry, BFSI Aspire for banking, financial services and insurance, and Customer Relationship for retail, hospitality and events. Each one combines online modules, practical training and placement assistance."
    },
    {
        question: "Who can join?",
        answer: "Graduates, undergraduates, diploma holders and even school dropouts can enrol. Everyone goes through a short HR screening that looks at communication, adaptability and job orientation."
    },
    {
        question: "How does Skillkafe help me find a job?",
        answer: "We partner with recruiting agencies and corporates, especially in tier 2 and tier 3 cities, and connect learners who complete their training with openings that match their skills."
    },
    {
        question: "Can I learn at my own pace?",
        answer: "Yes. Courses are online and self-paced, with recorded sessions you can revisit, video and e-learning content, live sessions with industry experts and faculty support whenever you need it."
    },
    {
        question: "What do recruiters get from Skillkafe?",
        answer: "Pre-screened candidates who are already trained for your sector. That means shorter onboarding, better productivity from the first day and stronger retention. We can also run dedicated hiring campaigns for your roles."
    },
    {
        question: "Is there a Skillkafe app?",
        answer: "Yes. The Skillkafe app on Google Play gives you industry-focused training and job placement support, and lets you search for and save jobs."
    }
]
